
/* eslint-disable max-len */
import * as $ from 'jquery';

import * as CanvasTemplates from 'src/integration/canvas/templates';
import * as CanvasPreview from 'src/integration/canvas/ui/preview';
import * as CanvasUiUtil from 'src/integration/canvas/ui/util';
import * as AllyIntegrationUtil from 'src/integration/util';
import { FileReference } from 'src/shared/rest/domain/FileReference';
import * as AllyUtil from 'src/shared/rest/util';
import * as TinyMcePasteCleanUpV4 from 'src/shared/tinymce/paste/CleanUpV4';

var userContentFileReferences = [];

var userContentWasHidden = false;

/**
 * Start polling the DOM for links to or embedded course content items
 *
 * @param  {Object}     pageInfo    The page information to help guide what features are available
 */
export function init(pageInfo) {
    if (pageInfo.editorId) {
        AllyIntegrationUtil.bindAllyAttributeMonitor(pageInfo.editorId);

        // Listen for paste events and remove any Ally score indicators or other injected HTML
        var cssSelectorsToRemoveFromPaste = [
            'a.ally-accessibility-score-indicator',
            'div.ally-user-content-dropdown'
        ].join(', ');
        TinyMcePasteCleanUpV4.fix(pageInfo.editorId, cssSelectorsToRemoveFromPaste);

    }

    pageInfo.findRichContentId = pageInfo.findRichContentId || function() {
        return null;
    };
    pageInfo.addRichContentAlternativeFormatLauncher = pageInfo.addRichContentAlternativeFormatLauncher || function() {};

    // Triggers an inline preview from the Ally dropdown
    document.addEventListener('click', function(ev) {
        const $el = $(ev.target).closest('.user_content');
        if ($el.length === 0) return;

        const id = $(this).attr('data-preview-id');

        /* Note that this needs to support both the new ENV.FEATURES.rce_better_file_previewing and the old preview icon
         * based launcher.
         * The new one looks like:
         *  <span class="instructure_file_holder link_holder ally-file-link-holder">
         *    [score indicator]
         *    <a class="file_preview_link" href="https://ally.instructure.com/courses/2465/files/67079?wrap=1"
         *       target="_blank" data-canvas-previewable="true" aria-expanded="false" aria-controls="preview_1"
         *       data-id="67079">
         *       scanned.pdf
         *    </a>
         *    [ally dropdown]
         *    <div role="region" id="preview_1" style="display: none;"></div>
         *  </span>
         *
         * The old one looks like:
         * <span class="instructure_file_holder link_holder">
         *   [score indicator]
         *   <a class="auto_open" href="https://ally.instructure.com/courses/2097/files/67214?wrap=1" target="_blank" data-canvas-previewable="true" data-id="67214">
         *     75426349_second.docx
         *   </a>
         *   <a class="file_preview_link" aria-hidden="true" href="https://ally.instructure.com/courses/2097/files/67214?wrap=1"
         *      title="Preview the document" style="margin-inline-start: 5px; display: inline;"
         *      data-id="67214"
         *      aria-expanded="false">
         *     <img src="/images/preview.png" alt="Preview the document">
         *   </a>
         *   [ally dropdown]
         *   <div role="region" id="preview_1" style="display: none;"></div>
         * </span>
         *
         *
         */
        const $previewLink = $(`.user_content div#${id}`).parent().find('a.file_preview_link');
        if ($previewLink.length > 0) {
            $previewLink[0].click();
        }
    }, { 'capture': true });

    poll(pageInfo);
}

/**
 * Poll the DOM for user content to enhance
 *
 * @param {Object} pageInfo The page information to help guide what features are available
 */
function poll(pageInfo) {
    var $userContent = $('.user_content');
    var collectedUserContentFileReferences = [];

    // If the user content panel disappeared then came back, it was probably a full reload of the DOM, which means
    // placement holders will have been removed. Clear the status so that when everything returns we will be able
    // to force restore the Ally indicators
    if (userContentWasHidden && $userContent.is(':visible')) {
        userContentWasHidden = false;

        // Wait for all the indicators to be cleared before we do anymore polling. If we poll right away, then there
        // is a chance that we repopulate `userContentFileReferences` based on the old DOM, and then when it resets
        // we won't detect any change that forces us to refresh the indicators
        AllyIntegrationUtil.wait(function() {
            return ($('.user_content .ally-accessibility-score-indicator').length === 0);
        }, function() {
            // Now that we know the old state was torn down, we can reset our file references which will cause
            // the poller to reset
            userContentFileReferences = [];
            setTimeout(poll, 0, pageInfo);
        });
        return;
    }
    userContentWasHidden = (!$userContent.is(':visible'));

    // Extract the file id from the download links
    getUserContentDownloadLinks().each(function() {
        var $link = $(this);

        // Collect the file id to retrieve its information from the REST API
        var fileId = $link.attr('data-id');
        collectedUserContentFileReferences.push(new FileReference(fileId));

        // Ensure the link is wrapped in a link_holder
        CanvasUiUtil.ensureWrappedInLinkHolder($link);
    });

    // Extract the file id from the embedded images
    $('img', $userContent).each(function() {
        var $img = $(this);
        var fileId = CanvasUiUtil.getFileId(this.src);
        if (fileId) {
            $img.attr('data-id', fileId);

            // Adjust image alt text due to bugs that used to be in Canvas and are now "fixed"
            CanvasUiUtil.fixImageAltText($img);

            if (pageInfo.findRichContentId($img)) {
                // Get the original values for any attributes on the $img that we have "worked around" (i.e., if we
                // applied alt text from another source to try and help remediate the file, we want to know what the
                // *original* value of that alt text was for purposes of giving instructor feedback)
                var allyWorkAroundSourceValues = AllyIntegrationUtil.getAllyWorkAroundSourceValues($img);
                var fileReference = FileReference.fromElement($img, fileId, allyWorkAroundSourceValues);
                collectedUserContentFileReferences.push(fileReference);
            } else {
                // If we cannot update rich content in this context, don't use an embedded file reference for
                // scoring or updates
                collectedUserContentFileReferences.push(new FileReference(fileId));
            }
        }
    });

    // Collect the rich content id, if any
    if ($userContent.length > 0 && !$userContent.attr('data-ally-richcontent-eid')) {
        const $child = $userContent.children().first();
        const richContentId = pageInfo.findRichContentId($child);
        if (richContentId) {
            collectedUserContentFileReferences.push({
                'data': () => ({
                    'id': richContentId,
                    'uploadType': 'RichContent'
                }),
                'id': () => richContentId
            });
        }
    }

    // Get the Ally information about the detected files if the
    // current list is different than the previous one
    var collectedUserContentFileReferenceIds = collectedUserContentFileReferences.map(function(fileReference) {
        return fileReference.id();
    });
    var userContentFileReferenceIds = userContentFileReferences.map(function(fileReference) {
        return fileReference.id();
    });

    // ALLYPD-719: if the drop down is too close to the bottom of the content container, it gets hidden in
    // scroll, so we increase the minimum height of the container. We cannot set height directly because the
    // container needs to increase size vertically when a preview is opened
    // This check is run every time we poll as there are too many other timing issues at play to do this once
    ensureUserContentIsLargeEnough($userContent);

    if (userContentFileReferenceIds.toString() !== collectedUserContentFileReferenceIds.toString()) {
        // If the file content on the UI has changed, get the new file information and force a refresh
        userContentFileReferences = collectedUserContentFileReferences;
        CanvasUiUtil.getFileInformation(userContentFileReferences, function(err) {
            if (!err) {
                enhanceUserContent(pageInfo);
            }

            // Check again for updates
            setTimeout(poll, 500, pageInfo);
        });
    } else {
        // Check again for updates
        setTimeout(poll, 500, pageInfo);
    }

    CanvasPreview.pollPreview(
        function() {
            return true;
        },
        function($previewOverlay) {
            return CanvasUiUtil.getFileId($previewOverlay.find('.ef-file-preview-header-download').attr('href'));
        }
    );
}

/** Checks whether the user_content container is large enough to accomodate the dropdown and grow if necessary */
function ensureUserContentIsLargeEnough($userContent) {
    const $lastDropdown = $('.user_content .ally-user-content-dropdown:last');
    const dropdownMenuHeight = 140;
    if ($lastDropdown.length === 1) {
        const userContentBottom = $userContent[0].offsetTop + $userContent.height();
        if ($lastDropdown[0].offsetTop > (userContentBottom - dropdownMenuHeight)) {
            $userContent.addClass('ally-enhancement');
        }
    }
}

/**
 * Enhance the user content page, if applicable.
 */
function enhanceUserContent(pageInfo) {
    // Enhance download links to files
    getUserContentDownloadLinks().each(function() {
        enhanceUserContentLink($(this), pageInfo);
    });

    // Enhance embedded course images
    $('.user_content img').each(function() {
        var $img = $(this);
        enhanceUserContentImage($img, pageInfo);
    });

    // Enhance rich content
    enhanceUserContentAsRichContent(pageInfo);
}

/**
 * Get the elements in the user content area that hold links to file downloads. Each `a` element with a link to a
 * file download will also be annotated with a `data-id` attribute holding the file id.
 */
function getUserContentDownloadLinks() {
    return $('.user_content').find('a').filter(function() {
        var $a = $(this);

        // Only return elements that are actual links to file downloads.
        var fileId = CanvasUiUtil.getFileId(this.href);
        var hasParsableFileId = !!fileId;

        // Cache the file id on the link element, if not done already
        if (hasParsableFileId && !$a.attr('data-id')) {
            $a.attr('data-id', fileId);
        }

        // Apply an extra whitelist logic as there are link elements that target a file download but use JS to
        // capture click events (e.g. the preview icon). To filter these out, require that a link has a known
        // quantifier.
        var hasKnownClass = $a.hasClass('instructure_file_link') || $a.hasClass('file_preview_link');
        var isWithinFileHolder = $a.parent().hasClass('instructure_file_holder');
        var isOldPreviewIcon = $a.hasClass('file_preview_link') && ($a.find('img[src="/images/preview.png"]').length > 0);
        var isDownloadButton = $a.hasClass('file_download_btn');
        var hasDataFileAttribute = $a.attr('data-api-returntype') === 'File';
        return (
            hasParsableFileId &&
            !isOldPreviewIcon &&
            !isDownloadButton &&
            (
                hasKnownClass ||
                hasDataFileAttribute ||
                isWithinFileHolder
            )
        );
    });
}

/**
 * Enhance a user content link with ally accessibility components. A user content file link is a link on a user
 * content page that has a link to a file and is wrapped in a "link_holder" span
 *
 * @param  {jQuery}    $fileLink    The jQuery object representing the link to enhance
 * @param  {PageInfo}  pageInfo     Current page metadata
 */
function enhanceUserContentLink($fileLink, pageInfo) {
    var fileReference = new FileReference($fileLink.attr('data-id'));
    var fileInfo = CanvasUiUtil.fileInformation()[fileReference.id()];
    if (!fileInfo) {
        // Only modify anything if we have accessibility metadata for the file
        return;
    }

    // Get the span that wraps the link. There should always be a span. Either added by us in a previous step or
    // it was added by Canvas when adding the preview/scribd button. Ensure that we've added the necessary class
    var $fileLinkHolder = $fileLink.parent();
    $fileLinkHolder.addClass('ally-file-link-holder');

    // Add the accessibility score indicator for instructors
    const hasScoreIndicator = $fileLinkHolder.find('.ally-accessibility-score-indicator').length > 0;
    if (AllyIntegrationUtil.hasScore(fileInfo) && canManageUserContent(pageInfo) && !hasScoreIndicator) {

        // If this link contains an image rather than text, overlay the file indicator on the bottom right corner of
        // the image
        var scoreIndicatorOptions = null;
        var $scoreIndicator = null;
        if ($fileLink.children().length > 0 && $fileLink.children().first().prop('tagName').toLowerCase() === 'img') {
            scoreIndicatorOptions = {
                'circle': true,
                'withFileType': true
            };
            $scoreIndicator = AllyIntegrationUtil.createScoreIndicator(fileInfo, scoreIndicatorOptions);
            var $img = $fileLink.children().first();
            $img.after($scoreIndicator);
            AllyIntegrationUtil.onCoordsChange($img, function(coords) {
                $scoreIndicator.offset({
                    'top': coords.bottom - $scoreIndicator.height() - 6,
                    'left': coords.right - ($scoreIndicator.width()) - 4
                });
            });
        } else {
            // Render/re-render the accessibility score indicator
            scoreIndicatorOptions = {
                'circle': true,
                'withFileType': false
            };
            $scoreIndicator = AllyIntegrationUtil.createScoreIndicator(fileInfo, scoreIndicatorOptions);
            $scoreIndicator.addClass('ally-accessibility-score-indicator-beside-link');
            $fileLinkHolder.prepend($scoreIndicator);
        }
    }

    // Canvas runs some custom javascript after page load that adds a download button and some preview logic. Wait until
    // that's ready before mucking with the DOM
    AllyIntegrationUtil.wait(function() {
        return canvasUserContentManipulationsAreReady($fileLinkHolder);
    }, function() {
        enhanceUserContentLinkWithDropdown($fileLinkHolder, $fileLink, fileInfo);
    });

}

/** Returns true when Canvas has completed its own set of on-the-fly link manipulations */
function canvasUserContentManipulationsAreReady($fileLinkHolder) {
    return (
        ($fileLinkHolder.find('a.file_download_btn').length > 0) ||
        ($fileLinkHolder.find('a.preview_in_overlay').length > 0) ||
        ($fileLinkHolder.find('img[src="/images/preview.png"]').length > 0)
    );
}

/**
 * Enhance a link to a file in the user content with a dropdown that has a preview, download and alternative format
 * option.
 *
 * This is done in the old Canvas editor (disable the RCE feature flag)
 */
function enhanceUserContentLinkWithDropdown($fileLinkHolder, $fileLink, fileInfo) {
    if (!isPlainTextLink($fileLinkHolder)) {
        // Bail out if this link has some funky layouting.
        return;
    }

    if (AllyIntegrationUtil.hasAvailableAlternativeFormats(fileInfo)) {
        // Remove the previous drop down, if any
        $fileLinkHolder.find('.ally-user-content-dropdown').remove();

        // Get a reference to the preview icon. This is no longer supported on ally.instructure.com, but some customers
        // still run with this option. This should be removed in the future.
        // It's behaviour is inferred from Instructure's public code:
        // https://github.com/instructure/canvas-lms/blob/master/public/javascripts/instructure.js#L205
        const $previewLink = $fileLinkHolder.find('a.file_preview_link:has(img[src="/images/preview.png"])');
        $previewLink.addClass('ally-hide-permanently');

        // The download URL can either be inferred from the direct download icon or from the link itself
        let downloadUrl = $fileLink.attr('href');
        const $downloadIcon = $fileLinkHolder.find('a.file_download_btn');
        if ($downloadIcon.length > 0) {
            downloadUrl = $downloadIcon.attr('href');
            $downloadIcon.remove();
        }

        // Preview options are 'overlay', 'inline' or null which indicates the preview is already showing
        const previewOptions = {
            'overlay': null,
            'inline': null
        };
        const previewUrl = $fileLink.attr('href');
        if (
            ($fileLink.attr('aria-expanded') === 'false') ||
            ($previewLink.length > 0)
        ) {
            // The preview needs to be displayed inline
            previewOptions.inline = true;
        } else if ($fileLink.hasClass('preview_in_overlay')) {
            // The preview pops out in an overlay
            previewOptions.overlay = true;
        }

        const previewDivId = $fileLinkHolder.find('div').attr('id');

        // Add the dropdown
        $fileLink.after(CanvasTemplates.apply('ally-user-content-dropdown', {
            'fileId': fileInfo.id,
            'downloadUrl': downloadUrl,
            'previewInline': previewOptions.inline,
            'previewOverlay': previewOptions.overlay,
            'previewUrl': previewUrl,
            'previewDivId': previewDivId
        }));
    }
}

/** Whether the given link holder is a straight-forward link with "plain" text allowing for simple text styling. */
function isPlainTextLink($fileLinkHolder) {
    // Add the alternative formats drop down, if there are any. To avoid causing undesirable stylistic effects to
    // advanced content layouts, we only add the drop down when the link does not contain elements that typically
    // substantially impact baseline or structure. If the link contains anything other than text and non-structural
    // elements, it's no longer a simple text link and the content creator probably intends for the link to be shown
    // in a certain way. Don't mess with that structure
    var nonStructuralTagNames = {
        'b': true,
        'em': true,
        'i': true,
        'label': true,
        'mark': true,
        'span': true,
        'strong': true,
        'sub': true,
        'sup': true,
        'tt': true,
        'u': true
    };
    var structuralChildren = $('a[href*="/files"]:first', $fileLinkHolder).children().filter(function(i, el) {
        // Any element that is not in our non-structural tag name map is considered structural
        return !nonStructuralTagNames[el.tagName.toLowerCase()] &&
            // Canvas Math equations (embedded as images) in links to a document should have a dropdown for the
            // document. In the above phase a score indicator would've been added, so make sure to exclude it when
            // checking for structural content.
            !$(el).is('img.equation_image') && !$(el).is('a.ally-accessibility-score-indicator');
    });
    return (structuralChildren.length === 0);
}

/**
 * Enhance an inline image in the Canvas user content page. Before any Ally enhancements, it is a simple `<img />`
 * tag on the page whose `src` parameter matched the regexp for a course file hosted by canvas (i.e.,
 * `CanvasUiUtil.getFileId()`).
 *
 * @param  {jQuery}     $img        The jQuery object holding the `<img />` tag to enhance
 * @param {PageInfo}    pageInfo    Current page metadata
 */
function enhanceUserContentImage($img, pageInfo) {
    const richContentId= pageInfo.findRichContentId($img);
    // The element was not annotated with an id, so do nothing
    var fileId = $img.attr('data-id');
    if (!fileId) {
        return;
    }

    var fileReference = new FileReference(fileId);
    if (richContentId) {
        // For purposes of establishing our file reference, ensure we extract the "source" information from the
        // element (i.e., attribute values that were on the element before Ally applied any workarounds)
        fileReference = FileReference.fromElement(
            $img,
            fileId,
            AllyIntegrationUtil.getAllyWorkAroundSourceValues($img)
        );
    }

    var fileInfo = CanvasUiUtil.fileInformation()[fileReference.id()];
    if (!fileInfo) {
        // Only modify anything if we have accessibility metadata for the file
        return;
    }

    // Apply work arounds to the image to enhance accessibility based on metadata we have access to. For example, if
    // this image was given alternative text elsewhere, we can apply that as the alternative text for now. However
    // the instructor will still be prompted to provide contextual alt text for the image
    var fileInfoAltText = AllyIntegrationUtil.getAlternativeText(fileInfo);
    if ($img.attr('alt') !== '' && !AllyUtil.isAltTextMeaningful($img.attr('alt'), fileInfo.name)
        && fileInfoAltText !== null) {
        AllyIntegrationUtil.applyAllyWorkAround($img, 'alt', fileInfoAltText);
    }

    // If this image is a seizure risk, add a seizure guard if there isn't one already
    if (fileInfo.seizureRisk) {
        AllyIntegrationUtil.addSeizureGuard(fileInfo, $img);
    }

    // If a score indicator should be shown, add/replace it in the image container. If the image is considered
    // embedded (basically, if we can do a rich alt text update), we always show the indicator. If the image is
    // not embedded and there is manual alt text applied, we don't show the indicator
    if (canManageUserContent(pageInfo) && AllyIntegrationUtil.shouldShowImageIndicator($img, fileReference, fileInfo)) {
        // Replace any score indicator with a new, potentially updated one
        var $existingIndicator = $img.nextAll(`.ally-accessibility-score-indicator[data-ally-file-id=${
            fileInfo.id }]:first`);
        if ($existingIndicator.length > 0 && $existingIndicator.prevAll('img:first').is($img)) {
            $existingIndicator.remove();
        }

        // Add the score indicator and keep it at the bottom-left of the image area. If the image is inside a link
        // that points to another file, the score indicator's tooltip label should indicate that the this score
        // indicator is for triggering the IF of the image
        var scoreIndicatorOptions = {
            'circle': true,
            'withFileType': ($img.parents('a').length > 0)
        };
        var $scoreIndicator = AllyIntegrationUtil.createScoreIndicator(fileInfo, scoreIndicatorOptions);
        $img.after($scoreIndicator);
        $scoreIndicator.ready(function () {
            var cssMaxHeight = $scoreIndicator.css('max-height');
            var padding = $scoreIndicator.css('padding');
            var height = null;
            if (cssMaxHeight != null && padding != null) {
                height = (cssMaxHeight.replace(/\D/g,'') - (padding.replace(/\D/g,'')));
            } else {
                height = $scoreIndicator.height();
            }
            AllyIntegrationUtil.onCoordsChange($img, function(coords) {
                $scoreIndicator.offset({
                    'top': coords.bottom - height,
                    'left': coords.left - 6
                });
            });
        });
    }
}

/**
 * Enhance the rich content with a launcher if its supported
 *
 * @param {PageInfo} pageInfo
 */
function enhanceUserContentAsRichContent(pageInfo) {
    const richContentId = pageInfo.findRichContentId($('.user_content').children().first());
    if (
        richContentId &&
        AllyIntegrationUtil.hasAvailableAlternativeFormats(CanvasUiUtil.fileInformation()[richContentId])
    ) {
        pageInfo.addRichContentAlternativeFormatLauncher(richContentId);
    }
}

/**
 * Determine if the current user can manage the course while on the user content page.
 *
 * @param {PageInfo}    pageInfo    Current page metadata
 */
function canManageUserContent(pageInfo) {
    return (
        (window.ENV.PAGE_RIGHTS && window.ENV.PAGE_RIGHTS.create) ||
        // Permission from pageInfo are used for Discussions/Announcement redesign, as ENV object is not present anymore
        (pageInfo.permissions && pageInfo.permissions.update) ||
        (window.ENV.DISCUSSION && window.ENV.DISCUSSION.PERMISSIONS && window.ENV.DISCUSSION.PERMISSIONS.MODERATE) ||
        (window.ENV.ASSIGNMENT_ID && canUpdateAssigment()) ||
        (window.ENV.QUIZ && window.ENV.QUIZ.can_update) ||
        (window.ENV.SYLLABUS_BODY && CanvasUiUtil.canManageCourse())
    );
}

/**
 * Whether the current user can update the currently displayed assignment. If the currently displayed page is not
 * an assignment or if the user can't update the assignment, this will return false.
 *
 * @return {Boolean} Whether the current user can update the currently displayed assignment
 */
function canUpdateAssigment() {
    return (window.ENV && window.ENV.PERMISSIONS && window.ENV.PERMISSIONS.assignment &&
        window.ENV.PERMISSIONS.assignment.update);
}
